import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import {
  Nav,
  Footer,
  ClosingV5,
  Eyebrow,
  PillarAccent,
} from "../App";
import { useContactPanel } from "../components/ContactPanel";
import { api } from "../admin/api";
import { useSeo } from "../lib/useSeo";

/* ============================================================
   /blog — Sunday Digital · Insights
   Editorial blog index. Same DNA as About / Showcase / Creative.
   Featured post hero + numbered "Recent" list + CTA band +
   Latest projects cross-link (carried over from the source page).
============================================================ */

/* ============================================================
   Posts data — fetched live from the CMS via GET /api/blog?public=true
   Slugs match sundaydigital.io URLs verbatim.
============================================================ */

/* ============================================================
   01 · Hero
============================================================ */
const BlogHero = ({ onPrimary }) => (
  <section
    id="top"
    data-testid="blog-hero"
    className="relative overflow-hidden bg-[#0B0F14]"
  >
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(900px 600px at 80% 20%, rgba(244,214,78,0.10), transparent 60%)",
      }}
    />
    <span
      aria-hidden
      className="absolute inset-0 opacity-[0.18] mix-blend-overlay pointer-events-none"
      style={{
        backgroundImage:
          "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.07) 1px, transparent 0)",
        backgroundSize: "3px 3px",
      }}
    />

    <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-20 md:pb-24">
      <div
        data-reveal="eyebrow"
        className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-12 md:mb-16"
      >
        <Link to="/" className="hover:text-[#F4D64E] transition-colors">Sunday</Link>
        <span className="w-1 h-1 rounded-full bg-neutral-700" />
        <span className="text-[#F4D64E]">Blog</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-14 items-end">
        <div className="lg:col-span-8">
          <span data-reveal="eyebrow">
            <Eyebrow>Insights · Field notes · Playbooks</Eyebrow>
          </span>
          <h1
            data-testid="blog-hero-title"
            className="font-display mt-7 text-white leading-[0.9] tracking-[-0.045em] font-black text-[3.5rem] sm:text-[5rem] md:text-[7.5rem] lg:text-[10rem] xl:text-[12rem]"
          >
            <span className="block">Blog</span>
            <span className="text-[#F4D64E]">.</span>
          </h1>
        </div>

        <div className="lg:col-span-4">
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="font-display text-[1.25rem] md:text-[1.4rem] font-bold tracking-[-0.005em] text-white leading-[1.4]"
          >
            <span>
              From trends to strategies — articles that bring you the latest
              buzz in Shopify ecommerce and web marketing.
            </span>
          </h2>
          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className="mt-6 text-[0.95rem] md:text-[1rem] text-neutral-400 leading-[1.65] max-w-[40ch]"
          >
            <span>
              Got a digital marketing objective or a store development goal
              you&apos;d like to achieve? Get in touch — we&apos;ll help you
              build it online.
            </span>
          </p>
          <button
            type="button"
            onClick={onPrimary}
            data-testid="blog-hero-cta"
            className="group mt-8 inline-flex items-center gap-2 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] hover:text-white transition-colors"
          >
            Get in touch
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   02 · Featured post
============================================================ */
const FeaturedPost = ({ post }) => (
  <section
    data-testid="blog-featured"
    className="relative bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="flex items-baseline gap-4 mb-12 md:mb-14">
        <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
          {post.n} · Featured
        </span>
        <span className="h-px flex-1 bg-white/[0.08] max-w-[80px]" />
        <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
          {post.category}
        </span>
      </div>

      <Link
        to={`/blog/${post.slug}`}
        data-testid={`blog-featured-${post.slug}`}
        className="group block"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-10 items-center">
          {/* Image */}
          <div className="lg:col-span-7 relative aspect-[16/10] overflow-hidden rounded-2xl border border-white/[0.06]">
            <img
              src={post.coverImg}
              alt={post.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
            />
            <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/55 via-transparent to-transparent" />
            <span aria-hidden className="absolute inset-0 ring-0 group-hover:ring-1 ring-inset ring-[#F4D64E]/30 rounded-2xl transition-all duration-500" />
            <span className="absolute top-6 right-6 inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
              <ArrowUpRight className="w-4 h-4" />
            </span>
          </div>

          {/* Copy */}
          <div className="lg:col-span-5">
            <h3 className="font-display text-[1.85rem] sm:text-[2.4rem] md:text-[2.85rem] lg:text-[3.25rem] font-black tracking-[-0.03em] leading-[1.05] text-white group-hover:text-[#F4D64E] transition-colors">
              {post.title}
            </h3>
            <p className="mt-7 text-[0.98rem] md:text-[1.05rem] text-neutral-400 leading-[1.7] max-w-[52ch]">
              {post.excerpt}
            </p>
            <div className="mt-9 inline-flex items-center gap-5 text-[0.7rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
              <span className="inline-flex items-center gap-2 text-[#F4D64E]">
                <Clock className="w-3.5 h-3.5" />
                {post.readTime}
              </span>
              <span className="w-px h-3 bg-white/[0.15]" />
              <span>Read article ↗</span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  </section>
);

/* ============================================================
   03 · Recent — numbered editorial list
============================================================ */
const RecentRow = ({ post }) => (
  <li>
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-row-${post.slug}`}
      className="group block border-t border-white/[0.08] last:border-b py-9 md:py-12"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-6 items-start">
        {/* Index + meta */}
        <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-4 md:gap-3">
          <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
            {post.n}
          </span>
          <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
            {post.category}
          </span>
        </div>

        {/* Title + excerpt */}
        <div className="md:col-span-7">
          <h3 className="font-display text-[1.5rem] sm:text-[1.85rem] md:text-[2.25rem] lg:text-[2.6rem] font-black tracking-[-0.025em] leading-[1.1] text-white group-hover:text-[#F4D64E] transition-colors">
            {post.title}
          </h3>
          <p className="mt-5 text-[0.95rem] md:text-[1rem] text-neutral-400 leading-[1.65] max-w-[58ch]">
            {post.excerpt}
          </p>
          <div className="mt-6 inline-flex items-center gap-4 text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
            <span className="inline-flex items-center gap-2 text-[#F4D64E]">
              <Clock className="w-3 h-3" />
              {post.readTime}
            </span>
            <span className="w-px h-3 bg-white/[0.15]" />
            <span className="group-hover:text-[#F4D64E] transition-colors">
              Read article ↗
            </span>
          </div>
        </div>

        {/* Thumbnail */}
        <div className="md:col-span-3 relative aspect-[5/4] overflow-hidden rounded-xl border border-white/[0.06]">
          <img
            src={post.coverImg}
            alt={post.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
          />
          <span aria-hidden className="absolute inset-0 ring-0 group-hover:ring-1 ring-inset ring-[#F4D64E]/30 rounded-xl transition-all duration-500" />
        </div>
      </div>
    </Link>
  </li>
);

const Recent = ({ recent, total }) => (
  <section
    data-testid="blog-recent"
    className="relative bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="flex items-end justify-between gap-6 mb-10 md:mb-14">
        <div>
          <Eyebrow>More articles</Eyebrow>
          <h2 className="mt-7 font-display text-[2rem] md:text-[2.75rem] lg:text-[3.5rem] font-black tracking-[-0.035em] leading-[1.05] text-white">
            <span>
              Recent{" "}
              <span className="italic font-normal text-neutral-400">field</span>{" "}
              <PillarAccent>notes</PillarAccent>.
            </span>
          </h2>
        </div>
        <span className="hidden md:block text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-500 tabular-nums">
          {recent.length} of {total}
        </span>
      </div>

      <ul>
        {recent.map((p) => (
          <RecentRow key={p.slug} post={p} />
        ))}
      </ul>
    </div>
  </section>
);

/* ============================================================
   04 · CTA band
============================================================ */
const CTABand = ({ onContact }) => (
  <section
    data-testid="blog-cta-band"
    className="relative bg-[#0F141A] border-y border-white/[0.06] overflow-hidden"
  >
    <span
      aria-hidden
      className="absolute -top-20 -right-20 w-[32rem] h-[32rem] rounded-full bg-[#F4D64E]/[0.08] blur-3xl"
    />
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-10 items-center">
        <div className="lg:col-span-8">
          <Eyebrow>Get in touch</Eyebrow>
          <h2 className="mt-7 font-display text-[2rem] sm:text-[2.6rem] md:text-[3.25rem] lg:text-[4rem] font-black tracking-[-0.035em] leading-[1.02] text-white">
            <span>
              Got a goal?{" "}
              <span className="italic font-normal text-neutral-400">Let&apos;s</span>{" "}
              <PillarAccent>build it</PillarAccent>.
            </span>
          </h2>
          <p className="mt-6 max-w-[58ch] text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.7]">
            Whether it&apos;s a Shopify build, a marketing engine, or a brand
            world — we&apos;ve done it before, and we&apos;ll do it with you.
          </p>
        </div>

        <div className="lg:col-span-4 flex flex-wrap items-center gap-4 lg:justify-end">
          <button
            type="button"
            onClick={onContact}
            data-testid="blog-cta-band"
            className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-4 rounded-md transition-colors"
          >
            Send a brief
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   05 · Latest Projects cross-link (carried from source)
============================================================ */
const LATEST_PROJECTS = [
  {
    n: "01",
    name: "Café Joyeux",
    tagline: "Coffee on a mission",
    img: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1200&q=80",
  },
  {
    n: "02",
    name: "Veronique Gabai",
    tagline: "Perfume & fragrances",
    href: "/showcase/veronique-gabai",
    img: "https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&w=1200&q=80",
  },
  {
    n: "03",
    name: "Madam Glam",
    tagline: "Gel & nail polish, art",
    img: "https://images.unsplash.com/photo-1604654894610-df63bc536371?auto=format&fit=crop&w=1200&q=80",
  },
  {
    n: "04",
    name: "Ming Yu Wang",
    tagline: "Designer statement jewelry",
    img: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&w=1200&q=80",
  },
];

const LatestProjects = () => {
  // Live random 4 from the published showcase list. Fetches once on mount
  // and shuffles client-side so each visit shows a different rotation.
  // Falls back to the hardcoded LATEST_PROJECTS list if the API is empty
  // or unreachable, so the section never goes blank.
  const [projects, setProjects] = useState(LATEST_PROJECTS);

  useEffect(() => {
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/showcase?public=true`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        if (cancelled || !Array.isArray(list) || list.length === 0) return;
        // Fisher–Yates shuffle (non-mutating).
        const a = [...list];
        for (let i = a.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [a[i], a[j]] = [a[j], a[i]];
        }
        const picked = a.slice(0, 4).map((d, i) => ({
          n: String(i + 1).padStart(2, "0"),
          name: d.name || "",
          tagline: d.industry || "",
          href: `/showcase/${d.slug}`,
          img: d.heroImg || "",
        }));
        if (picked.length > 0) setProjects(picked);
      })
      .catch(() => {
        /* keep fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      data-testid="blog-latest-projects"
      className="relative bg-[#0B0F14] border-t border-white/[0.06]"
    >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="flex items-end justify-between gap-6 mb-14 md:mb-16">
        <div>
          <Eyebrow>Latest projects</Eyebrow>
          <h2 className="mt-7 font-display text-[2rem] md:text-[2.75rem] lg:text-[3.5rem] font-black tracking-[-0.035em] leading-[1.05] text-white">
            <span>
              The work{" "}
              <span className="italic font-normal text-neutral-400">behind</span>{" "}
              <PillarAccent>the words</PillarAccent>.
            </span>
          </h2>
        </div>
        <Link
          to="/showcase"
          className="group inline-flex items-center gap-2 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] hover:text-white transition-colors shrink-0"
          data-testid="blog-all-projects"
        >
          All projects
          <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </div>

      <ul className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
        {projects.map((p) => {
          const inner = (
            <div className="group relative">
              <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/[0.06] mb-5">
                <img
                  src={p.img}
                  alt={p.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                />
                <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/60 via-transparent to-transparent" />
                <span className="absolute top-5 left-5 text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
                  {p.n}
                </span>
              </div>
              <div className="font-display text-[1.05rem] md:text-[1.2rem] font-black tracking-[-0.015em] uppercase text-white group-hover:text-[#F4D64E] transition-colors leading-[1.2]">
                {p.name}
              </div>
              <div className="mt-1 text-[0.85rem] text-neutral-400 italic">
                {p.tagline}
              </div>
            </div>
          );
          return (
            <li key={p.n} data-testid={`blog-project-${p.n}`}>
              {p.href ? <Link to={p.href}>{inner}</Link> : <div>{inner}</div>}
            </li>
          );
        })}
      </ul>
    </div>
  </section>
  );
};

/* ============================================================
   PAGE
============================================================ */
export default function BlogPage() {
  const { open: openContact } = useContactPanel();
  const [posts, setPosts] = useState(null);

  useSeo({
    route: "/blog",
    fallback: {
      title: "Blog — Sunday Digital",
      description: "Insights, playbooks, and notes from the studio.",
    },
  });
  useEffect(() => {
    document.title = "Blog — Sunday Digital";
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    api
      .blogList()
      .then((all) => {
        const pub = (all || []).filter((p) => p.published);
        setPosts(pub);
      })
      .catch(() => setPosts([]));
  }, []);

  useEffect(() => {
    const root = document.querySelector('[data-variant="v5"]');
    if (!root) return;
    const els = root.querySelectorAll("[data-reveal]");
    if (!els.length) return;
    if (typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div
      data-testid="blog-page"
      data-variant="v5"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        <BlogHero onPrimary={openContact} />
        {posts && posts.length > 0 ? (
          <>
            <FeaturedPost post={posts.find((p) => p.featured) || posts[0]} />
            <Recent
              recent={posts.filter(
                (p) => p.slug !== (posts.find((q) => q.featured) || posts[0]).slug
              )}
              total={posts.length}
            />
          </>
        ) : posts === null ? (
          <section className="bg-[#0B0F14] py-32 text-center">
            <span className="text-[0.7rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
              Loading articles…
            </span>
          </section>
        ) : (
          <section className="bg-[#0B0F14] py-32 text-center">
            <span className="text-sm text-neutral-500">No articles yet.</span>
          </section>
        )}
        <CTABand onContact={openContact} />
        <LatestProjects />
        <ClosingV5 />
      </main>
      <Footer />
    </div>
  );
}
